import { CheckIcon, CloseIcon, DownloadIcon, SearchIcon, ViewIcon, DeleteIcon, WarningTwoIcon } from "@chakra-ui/icons";
import {
    Box,
    Button,
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    DrawerBody,
    DrawerHeader,
    Flex,
    HStack,
    Heading,
    IconButton,
    Image,
    Input,
    InputGroup, InputRightElement,
    Link,
    Text,
    VStack,
    useDisclosure,
    useToast
} from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import AppViewDrawer from "components/AppViewDrawer";
import AppViewListItem from "components/AppViewListItem";
import LoadingModal from "components/LoadingModal";
import { Pagination } from "components/Pagination";
import { useApi } from "hooks/useApi";
import { useAuthentication } from "hooks/useAuthentication";
import { useCache } from "hooks/useCache";
import { useCallback, useContext, useMemo, useRef, useState } from "react";
import Zoom from "react-medium-image-zoom";
import { useLocation, useNavigate } from "react-router-dom";
import { Store } from "store";
import { closeLoader, openLoader, showToast, toastTypes } from "utils";
import { ReactTable } from "../../components/ReactTable";

type Table3RowType = {
  id: number;
  expiry_date: string;
  manufacturing_date: string;
  product_id: boolean;
  product_name: string;
  product_image: string;
  company_id: boolean;
  status: string;
  company_name: string;
  company_image: string;
  number_of_qr_codes: string;
  created_at: string;
  processed_by: string;
  has_qr_codes: boolean;
  codes_pdf: string;
};

export default function QrCodeDashboardSection() {
  const location = useLocation();
  const {startDate, endDate} = location.state as any || {};
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose,
  } = useDisclosure();
  const cancelRef = useRef<any>();
  const [isDeleting, setIsDeleting] = useState(false);
  const loadingRef = useRef<any>(null);
  const { dispatch } = useContext(Store);
  const toast = useToast();
  const [activeItem, setActiveItem] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const navigate = useNavigate();
  const { hasPermission } = useAuthentication();
  const [searchBarValue34, setSearchBarValue34] = useState("");
  const { getQrCodeRequests, generateQrCodes, deleteQrCodeRequest } = useApi();
  const { data: qrCodeRequests, mutate } = useCache("qrCodeRequests", () =>
    getQrCodeRequests(page, searchBarValue34, startDate, endDate)
  );
  const handleSearch = (event: any) => {
    const value = event.target.value;
    mutate(getQrCodeRequests(page, value, startDate, endDate));
    // if (event.key === "Enter") {
    //   setSearchBarValue34(value);
    // } else {
    //   setSearchBarValue34(value);
    // }
  };
  const handleClearSearch = (event: any) => {
    setSearchBarValue34("");
    mutate(getQrCodeRequests(page, "", startDate, endDate));
  };

  const handleJumpToPage = (pg: number) => {
    setPage(pg);
    mutate(getQrCodeRequests(pg, "", startDate, endDate));
  };
  const handleSetNextPage = () => {
    if (
      qrCodeRequests.length > 0 &&
      qrCodeRequests[0].num_of_pages >= page + 1
    ) {
      setPage((prev: any) => prev + 1);
      mutate(getQrCodeRequests(page + 1,"", startDate, endDate));
    }
  };
  const handleSetPrevPage = () => {
    if (qrCodeRequests.length > 0 && page > 0) {
      setPage((prev: any) => prev - 1);
      mutate(getQrCodeRequests(page + 1, "", startDate, endDate));
    }
  };

  const handleViewItem = useCallback(
    (item: any) => {
      setActiveItem(item);
      onOpen();
    },
    [onOpen, setActiveItem]
  );

  const handleGenerateQrCodes = useCallback(
    async (item: any) => {
      openLoader(loadingRef);
      const response = await generateQrCodes({ qr_code_request_id: item.id });
      if (response?.id) {
        dispatch(
          showToast({
            toast: toast,
            message: "Codes Qr générés avec succès",
            status: toastTypes.success,
          })
        );
      }else{
        dispatch(
          showToast({
            toast: toast,
            message: "Impossible de générer les codes Qr",
            status: toastTypes.error,
          })
        );
      }
      mutate(getQrCodeRequests(page,"", startDate, endDate))
      closeLoader(loadingRef)
    },
    [dispatch, generateQrCodes, getQrCodeRequests, mutate, page, toast, startDate, endDate]
  );

  const handleDownloadAll = (downloadLink:string) => {
    const pdfUrl = downloadLink;
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.target = "_blank"
    link.download = "document.pdf"; // specify the filename
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

  const table3Columns = useMemo(() => {
    const table3ColumnHelper = createColumnHelper<Table3RowType>();
    return [
      table3ColumnHelper.accessor("company_name", {
        cell: (info) => (
          <Flex justifyContent="center">
            <Flex gap="8px" w="100%" alignItems="center" px="12px" py="0px">
              {/* <Checkbox value="true" /> */}
              <Image
                src={info.row.original.company_image as unknown as string}
                alt="Client Image"
                h="32px"
                w="32px"
                fit="contain"
                borderRadius="16px"
              />
              <Text>{info.getValue<string>()}</Text>
            </Flex>
          </Flex>
        ),
        header: (info) => (
          <Flex p="12px">
            <Flex
              gap="8px"
              w={{ md: "42%", base: "100%" }}
              alignItems="center"
              px="12px"
            >
              {/* <Checkbox value="true" /> */}
              <Heading as="h6">Client</Heading>
            </Flex>
          </Flex>
        ),
        meta: { width: "170px" },
      }),
      table3ColumnHelper.accessor("product_name", {
        cell: (info) => (
          // <Box flex={1}>
          <Flex gap="8px" alignItems="center" px="12px" py="0px">
            <Image
              src={info.row.original.product_image as unknown as string || info.row.original.company_image as unknown as string}
              alt="Client Image"
              h="32px"
              w="32px"
              fit="contain"
              borderRadius="8px"
            />
            <Text>{info.getValue<string>()}</Text>
          </Flex>
          // </Box>
        ),
        header: (info) => (
          <Flex
            gap="8px"
            w={{ md: "42%", base: "100%" }}
            alignItems="center"
            px="25px"
          >
            <Heading as="h6">Type</Heading>
          </Flex>
        ),
        meta: { width: "212px" },
      }),
      table3ColumnHelper.accessor("number_of_qr_codes", {
        cell: (info) => (
          // <Box flex={1}>
          <Flex gap="8px" alignItems="center" px="12px" py="0px">
            <Text>{info.getValue<string>()}</Text>
          </Flex>
          // </Box>
        ),
        header: (info) => (
          <Flex
            gap="8px"
            w={{ md: "42%", base: "100%" }}
            alignItems="center"
            px="25px"
          >
            <Heading as="h6">Quantité</Heading>
          </Flex>
        ),
        meta: { width: "100px" },
      }),
      table3ColumnHelper.accessor("created_at", {
        cell: (info) => (
          <Box flex={1}>
            <Flex gap="8px" alignItems="center" px="12px" py="0px">
              <Text>{info.getValue<string>()}</Text>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1} px="25px" py="14px">
            <Heading as="h6">Date de création</Heading>
          </Flex>
        ),
        meta: { width: "220px" },
      }),
      table3ColumnHelper.accessor("processed_by", {
        cell: (info) => (
          <Box flex={1}>
            <Flex gap="8px" alignItems="center" px="12px" py="0px">
              <Text>{info.getValue<string>()}</Text>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1} px="25px" py="14px">
            <Heading as="h6">Généré par</Heading>
          </Flex>
        ),
        meta: { width: "150px" },
      }),
      table3ColumnHelper.accessor("status", {
        cell: (info) => (
          <Box flex={1}>
            <Flex gap="8px" alignItems="center" px="12px" py="0px">
              <Text color={info.row.original.has_qr_codes ? "green_360" : "yellow"}>{info.row.original.has_qr_codes ? "Généré" : "pas à jour"}</Text>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1} px="25px" py="14px">
            <Heading as="h6">État</Heading>
          </Flex>
        ),
        meta: { width: "100px" },
      }),
      table3ColumnHelper.accessor("id", {
        cell: (info) => (
          <HStack gap={"2px"} width={"100%"} justifyContent={"flex-end"}>
            {hasPermission("qrcoderequest", "get") && (
              <IconButton
                variant="outline"
                colorScheme="gray_stroke_0"
                aria-label="view"
                size={"sm"}
                icon={<ViewIcon />}
                borderRadius={"4px"}
                onClick={() => handleViewItem(info.row.original)}
              />
            )}
            {hasPermission("qrcoderequest", "generate_qr_code") &&
              !info.row.original.has_qr_codes && (
                <IconButton
                  variant="outline"
                  colorScheme="green_360"
                  aria-label="view"
                  size={"sm"}
                  icon={<CheckIcon />}
                  borderRadius={"4px"}
                  onClick={() => handleGenerateQrCodes(info.row.original)}
                />
              )}
            {hasPermission("qrcodes", "get") &&
              info.row.original.has_qr_codes && (
                <IconButton
                  variant="outline"
                  colorScheme="gray_stroke_0"
                  aria-label="view"
                  size={"sm"}
                  display={"none"}
                  icon={
                    <Image
                      src="images/qr_code_icon.png"
                      alt="qr code icon"
                      width={3}
                      height={3}
                    />
                  }
                  borderRadius={"4px"}
                  onClick={() => navigate(`/qrcodes/${info.getValue()}`)}
                />
              )}
            {hasPermission("qrcodes", "get") &&
              info.row.original.codes_pdf && (
                <IconButton
                  variant="outline"
                  colorScheme="gray_stroke_0"
                  aria-label="view"
                  size={"sm"}
                  icon={<DownloadIcon />}
                  borderRadius={"4px"}
                  onClick={() => handleDownloadAll(info.row.original?.codes_pdf)}
                />
              )}
            {hasPermission("qrcoderequest", "delete") && (
              <IconButton
                variant="outline"
                colorScheme="red_origin"
                aria-label="delete"
                size={"sm"}
                icon={<DeleteIcon />}
                borderRadius={"4px"}
                onClick={() => {
                  setActiveItem(info.row.original);
                  onConfirmOpen();
                }}
                isDisabled={isDeleting && activeItem?.id === info.row.original.id}
                isLoading={isDeleting && activeItem?.id === info.row.original.id}
              />
            )}
          </HStack>
        ),
        header: (info) => (
          <Flex justifyContent={"flex-end"} px="15px" py="14px">
            <Heading as="h6">Action</Heading>
          </Flex>
        ),
        meta: { width: "100px" },
      }),
    ];
  }, [navigate, handleViewItem, hasPermission, handleGenerateQrCodes]);

  return (
    <>
      {/* client management section */}
      <Flex
        flexDirection="column"
        alignItems="start"
        px={{ base: "20px", sm: "24px" }}
      >
        <Flex gap="4px" alignSelf="stretch" flexDirection="column">
          <Flex
            justifyContent="center"
            alignItems="center"
            flexDirection={{ base: "column", sm: "row" }}
          >
            <Heading size="text2xl" as="h5" fontFamily="Arimo">
            Génération de Codes QR
            </Heading>
            <Flex
              pl="56px"
              pr="130px"
              gap="13px"
              flex={1}
              justifyContent="flex-end"
              alignItems="center"
              alignSelf={{ base: "stretch", sm: "auto" }}
              flexDirection={{ base: "column", sm: "row" }}
              px={{ md: 0, base: "20px" }}
            >
              
              {hasPermission("qrcoderequest", "create") && (
                <>
                <Button
                  size="xl"
                  variant="outline"
                  colorScheme="red_origin"
                  leftIcon={
                    <Image
                      src="images/img_handshake_deal_svgrepo_com_red_origin.svg"
                      alt="Handshake-deal Svgrepo.com"
                    />
                  }
                  fontWeight={700}
                  gap="8px"
                  minW="128px"
                  borderRadius="8px"
                  as={Link}
                  href="/qrcode-requests/add"
                >
                  Faire une commande liée au produit
                </Button>
                <Button
                  size="xl"
                  variant="outline"
                  colorScheme="red_origin"
                  leftIcon={
                    <Image
                      src="images/img_handshake_deal_svgrepo_com_red_origin.svg"
                      alt="Handshake-deal Svgrepo.com"
                    />
                  }
                  fontWeight={700}
                  gap="8px"
                  minW="128px"
                  borderRadius="8px"
                  as={Link}
                  href="/qrcode-requests/documents/add"
                >
                  Faire une commande liée au document
                </Button>
                </>
              )}
            </Flex>
          </Flex>
        </Flex>
        <Box
          mt="16px"
          bg="white"
          w={{ md: "100%", base: "100%" }}
          borderRadius="10px"
        >
          <Flex
            bg="white"
            justifyContent="center"
            alignItems="center"
            px="12px"
            py="16px"
            flexDirection={{ md: "row", base: "column" }}
          >
            <Flex
              flex={1}
              alignItems="center"
              alignSelf={{ md: "auto", base: "stretch" }}
              flexDirection={{ base: "column", sm: "row" }}
            >
              <InputGroup w={{ base: "100%", sm: "30%" }}>
                
                <Input
                  placeholder={`Rechercher un client ou produit`}
                  value={searchBarValue34}
                  onChange={(event:any) => setSearchBarValue34(event.currentTarget.value)}
                  gap="8px"
                  borderWidth="1px"
                  borderRadius="8px"
                />
                <InputRightElement>
                  {searchBarValue34?.length > 0 ? (
                    <CloseIcon onClick={handleClearSearch} />
                  ) : null}
                </InputRightElement>
              </InputGroup>
                <IconButton onClick={handleSearch} ml={2} borderRadius={"10px"} py={"19px"} border={"1px solid #e2e4e6"} aria-label="search" icon={<SearchIcon/>} justifyContent={"center"} alignItems={"center"} />
            </Flex>
            <Image
              src="images/img_dot_menu.svg"
              alt="Menu Image"
              h="24px"
              w={{ md: "24px", base: "100%" }}
            />
          </Flex>
          <ReactTable
            size="sm"
            headerCellProps={{ bg: "white" }}
            tableProps={{
              borderColor: "neutral.600",
              borderTopWidth: "1px",
              borderStyle: "solid",
            }}
            columns={table3Columns as any}
            data={qrCodeRequests}
          />
          <Pagination
            pageIndex={page}
            pageSize={pageSize}
            setNextPageIndex={handleSetNextPage}
            setPrevPageIndex={handleSetPrevPage}
            setPageSize={setPageSize}
            jumpToPage={handleJumpToPage}
            numberOfPages={
              qrCodeRequests &&
              qrCodeRequests?.length > 0 &&
              qrCodeRequests[0].num_of_pages
            }
          />
        </Box>
      </Flex>
      <LoadingModal ref={loadingRef} />
      <AppViewDrawer isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
        <DrawerHeader>Fiche de demande de Codes QR</DrawerHeader>

        <DrawerBody>
          <VStack alignItems={"flex-start"} gap={"15px"}>
            <VStack alignItems={"center"} gap={"5px"} width={"100%"}>
              <Flex gap="8px" padding={"1px"}>
              <Zoom>
                <Image
                  src={activeItem?.product_image || activeItem?.company_image}
                  fallbackSrc="/images/dummy.png"
                  alt="logo Image"
                  h="100px"
                  w="100px"
                  fit="contain"
                  borderRadius="16px"
                />
                </Zoom>
              </Flex>
            </VStack>
            <AppViewListItem label="Produit" value={activeItem?.product_name} />
            <AppViewListItem label="Client" value={activeItem?.company_name} />
            <AppViewListItem
              label="Quantité de Codes QR commandée"
              value={activeItem?.number_of_qr_codes}
            />
            <AppViewListItem
              label="Créé le..."
              value={activeItem?.created_at}
            />
            {/* <AppViewListItem
              label="Mis à jour le..."
              value={activeItem?.expiry_date}
            /> */}
          </VStack>
        </DrawerBody>
      </AppViewDrawer>

      <AlertDialog isOpen={isConfirmOpen} leastDestructiveRef={cancelRef} onClose={onConfirmClose}>
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="16px" boxShadow="2xl">
            <AlertDialogHeader fontSize="lg" fontWeight="bold" display="flex" alignItems="center" gap={2}>
              <WarningTwoIcon color="red.400" />
              Confirmation de suppression
            </AlertDialogHeader>

            <AlertDialogBody fontSize="md" color="gray.700" textAlign="center">
              <VStack spacing={3} align="center">
                <WarningTwoIcon boxSize={16} color="red.400" />
                <Text fontSize="md">
                  Voulez-vous vraiment supprimer cette demande de Codes QR (#{activeItem?.id}) ?
                </Text>
                <Text fontSize="md">Cette action est irréversible.</Text>
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onConfirmClose} variant="outline" colorScheme="gray_stroke_0" borderRadius="8px" isDisabled={isDeleting}>
                Annuler
              </Button>
              <Button colorScheme="red_origin" ml={3} leftIcon={<DeleteIcon />} borderRadius="8px" isLoading={isDeleting} loadingText="Suppression..." onClick={async () => {
                if (!activeItem?.id) return;
                try {
                  setIsDeleting(true);
                  await deleteQrCodeRequest(activeItem.id);
                  onConfirmClose();
                  onClose();
                  mutate(getQrCodeRequests(page, searchBarValue34, startDate, endDate));
                } finally {
                  setIsDeleting(false);
                }
              }}>
                Supprimer
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
